'use client';

import { Button, Tooltip, cn } from '@heroui/react';
import { Icon } from '@iconify/react';
import React, { KeyboardEvent, useEffect, useRef } from 'react';

import PromptInput from './prompt-input';

const ideas = [
  {
    title: 'Create a blog post about HeroUI',
    description: 'explain it in simple terms',
  },
  {
    title: 'Give me 10 ideas for my next blog post',
    description: 'include only the best ideas',
  },
  {
    title: 'Compare HeroUI with other UI libraries',
    description: 'be as objective as possible',
  },
  {
    title: 'Write a text message to my friend',
    description: 'be polite and friendly',
  },
];
export const PromptInputWithBottomActions = ({
  onSubmit,
  isEnabled,
}: {
  onSubmit: (value: string) => void;
  isEnabled: boolean;
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [prompt, setPrompt] = React.useState<string>('');

  useEffect(() => {
    if (isEnabled) {
      inputRef.current?.focus();
    }
  }, [isEnabled]);

  const onPromptChange = (formData: any) => {
    const prompt = formData.get('prompt');
    if (prompt) {
      onFormSubmit(prompt);
    }
  };

  const onFormSubmit = (prompt: string) => {
    if (prompt.length) {
      onSubmit(prompt);
      setPrompt('');
      formRef.current?.reset();
      inputRef.current?.focus();
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <form
        ref={formRef}
        action={onPromptChange}
        className="flex w-full flex-col items-start rounded-medium bg-default-100 transition-colors hover:bg-default-200/70"
      >
        <PromptInput
          ref={inputRef}
          name="prompt"
          classNames={{
            inputWrapper: '!bg-transparent shadow-none',
            innerWrapper: 'relative',
            input: 'pt-1 pl-2 pb-6 !pr-10 text-medium',
          }}
          endContent={
            <div className="flex items-end gap-2 h-8 w-8">
              <Tooltip showArrow content="Send message">
                <Button
                  isIconOnly
                  color={!prompt ? 'default' : 'primary'}
                  isDisabled={!isEnabled || !prompt}
                  radius="lg"
                  size="sm"
                  variant="solid"
                  type="submit"
                >
                  <Icon
                    className={cn('[&>path]:stroke-[2px]', !prompt ? 'text-default-600' : 'text-primary-foreground')}
                    icon={isEnabled ? 'solar:arrow-up-linear' : 'solar:refresh-bold-duotone'}
                    width={20}
                  />
                </Button>
              </Tooltip>
            </div>
          }
          minRows={2}
          radius="lg"
          value={prompt}
          variant="flat"
          onValueChange={setPrompt}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (isEnabled) {
                onFormSubmit(prompt);
              }
            }
          }}
        />
        <div className="flex w-full items-center justify-between  gap-2 overflow-hidden px-4 pb-2">
          <p className="py-1 text-tiny text-default-400 ml-auto">{prompt.length}/2000</p>
        </div>
      </form>
    </div>
  );
};

export default PromptInputWithBottomActions;
